import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IObserverOffice } from '@platform/dao';
import { useDeleteObserverOfficeMutation, useResetObserverOfficeMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import { accountErrorKey } from './accountErrorKey';
import type { IOfficeActions } from './IOfficeActions';

/**
 * Скидання й видалення демо-офісу спостерігача (рішення 5 етапу 7): підтвердження з назвою офісу (UIUX confirmations), запит, повідомлення про
 * результат. Скинутий офіс сервер створює заново з новим ідентифікатором — перелік оновлюється запитом (тег каталогу просторів).
 */
export function useOfficeActions(): IOfficeActions {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [resetOffice] = useResetObserverOfficeMutation();
  const [deleteOffice] = useDeleteObserverOfficeMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const act = useCallback((office: IObserverOffice, kind: 'reset' | 'delete') => {
    if (!window.confirm(t(kind === 'reset' ? 'sysAccounts.offices.confirmReset' : 'sysAccounts.offices.confirmDelete', { name: office.name }))) return;
    setBusy(office.workspaceId);
    const call = kind === 'reset' ? resetOffice(office.workspaceId).unwrap() : deleteOffice(office.workspaceId).unwrap();
    call.then(() => dispatch(toastShown({ kind: 'success', text: t(kind === 'reset' ? 'sysAccounts.offices.resetToast' : 'sysAccounts.offices.deletedToast', { name: office.name }) })))
      .catch((e: unknown) => dispatch(toastShown({ kind: 'error', text: t(accountErrorKey(e)) })))
      .finally(() => setBusy(null));
  }, [deleteOffice, dispatch, resetOffice, t]);
  return { busy, reset: useCallback((o: IObserverOffice) => act(o, 'reset'), [act]), remove: useCallback((o: IObserverOffice) => act(o, 'delete'), [act]) };
}
